// Relative imports (not `@/`) so bun can test this module.
import { getHexTileMapKey } from '../hexmap/model/keys';
import {
  type Cell,
  canTakeJob,
  type EntityId,
  resolveJob,
  tileResources,
  workerSlots,
} from '../sim';
import { assignedWorkers } from './activity';
import type { Frame } from './frame';
import { resourceIcon } from './resources';
import { tickWorkers } from './tickCache';

/** Glyph for a build job (a gather job shows its resource's). */
export const BUILD_ICON = '🔨';

/**
 * What a right-click on one tile means for the current selection when some of
 * it can work there: the `assign` it would send and what to show over the
 * tile. `workers` take the job, `others` keep the usual right-click order.
 */
export interface WorkTarget {
  kind: 'gather' | 'build';
  icon: string;
  /** Hover label, e.g. "Assign to Forestry 3/5" or "Build Farm". */
  text: string;
  /** The `assign` target: an unfinished site's id, else the clicked tile. */
  target: Cell | EntityId;
  workers: EntityId[];
  others: EntityId[];
}

/**
 * The work `cell` offers `selected` (own units), or null (then right-click is
 * a move or attack as ever, also over a rival's building or unit it knows of). The job is the sim's own `resolveJob`, which is
 * fog-safe by design (explored tiles only, never the amount left, a rival's
 * building only counts in sight); the slot counts are of the player's own
 * building as the view shows it.
 */
export function workTargetAt(
  frame: Frame,
  selected: readonly EntityId[],
  cell: Cell
): WorkTarget | null {
  if (selected.length === 0) return null;
  const { real, view, ctx, rules, playerId, fog } = frame;
  // A rival's building the player knows of (even out of sight) or a rival unit
  // in sight on the tile is what a right-click there is aimed at: the usual
  // attack or move stands.
  const key = getHexTileMapKey(cell);
  const known = frame.knownBuilding(key);
  if (known && known.owner !== playerId) return null;
  if (
    fog.visible.has(key) &&
    Object.values(view.units).some(
      (u) => u.owner !== playerId && !u.inside && getHexTileMapKey(u.pos) === key
    )
  ) {
    return null;
  }
  const check = resolveJob(real, ctx, playerId, cell);
  if (!check.ok) return null;
  const workers: EntityId[] = [];
  const others: EntityId[] = [];
  for (const id of selected) {
    // Real state: a disguised rival spy never counts as a worker of ours.
    const u = real.units[id];
    (u && u.owner === playerId && canTakeJob(ctx, u, check.job) ? workers : others).push(id);
  }
  if (workers.length === 0) return null;

  const { job } = check;
  if (job.kind === 'build') {
    const name = rules.buildings[job.building.type]?.name ?? job.building.type;
    return {
      kind: 'build',
      icon: BUILD_ICON,
      text: `Build ${name}`,
      target: job.building.id,
      workers,
      others,
    };
  }
  const building = frame.knownBuilding(getHexTileMapKey(job.target));
  const def = building?.owner === playerId ? rules.buildings[building.type] : undefined;
  if (building && def?.provides) {
    const used = assignedWorkers(view, rules, building, tickWorkers(view, rules)).total;
    return {
      kind: 'gather',
      icon: resourceIcon(def.provides.resource),
      text: `Assign to ${def.name} ${used}/${workerSlots(rules, building)}`,
      target: cell,
      workers,
      others,
    };
  }
  const resource = tileResources(real, ctx, cell)?.resourceId;
  return {
    kind: 'gather',
    icon: resource ? resourceIcon(resource) : '⛏️',
    text: `Gather ${(resource && rules.resources[resource]?.name.toLowerCase()) ?? 'resource'}`,
    target: cell,
    workers,
    others,
  };
}

/**
 * Where the hover label's top-left goes: just right of and below the pointer,
 * flipped to its left or above when it would leave the `view` (w × h) box.
 */
export function labelPosition(
  pointer: { x: number; y: number },
  label: { w: number; h: number },
  view: { w: number; h: number }
): { x: number; y: number } {
  const gapX = 18;
  const gapY = 14;
  const x = pointer.x + gapX + label.w > view.w ? pointer.x - gapX - label.w : pointer.x + gapX;
  const y = pointer.y + gapY + label.h > view.h ? pointer.y - gapY - label.h : pointer.y + gapY;
  return { x: Math.max(0, x), y: Math.max(0, y) };
}
